import React, { useCallback, useState } from "react";

import { Loader } from "@editor/components/common/Loader";
import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc, trpcClient, trpcUtils } from "@editor/utils/trpc";

import { useWebContainer } from "@/features/web-container/context";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Popover from "@replo/design-system/components/popover/Popover";
import {
  Card,
  CardContent,
} from "@replo/design-system/components/shadcn/core/card";
import { CheckIcon, MoreHorizontal, XIcon } from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";

import { useChatStoreActions, useViewingVersionId } from "../store/chatStore";

interface ChangeBlockProps {
  message?: string;
  isLoading?: boolean;
  hasError?: boolean;
  appliedDiffs?: string[];
  fileVersionId?: string;
  fileVersionSummary?: string;
}

const ChangeBlock = ({
  message,
  isLoading,
  hasError,
  appliedDiffs,
  fileVersionId,
  fileVersionSummary,
}: ChangeBlockProps) => {
  const isDebug = useIsDebugMode();
  const projectId = useCurrentProjectId();
  const { pageId } = useParams();
  const { user } = useCurrentUser();
  const { mountAdditionalFiles } = useWebContainer();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  // NOTE (Bobby: 2025-07-09): This state's currently only used in this component but will be used in other components immediately after this, so putting it in the store first.
  // e.g. Whenever a new version is created, whether its from chat or modifiers, we'll wnat to set the viewingVersionId to the new version
  const { setViewingVersionId } = useChatStoreActions();
  const viewingVersionId = useViewingVersionId();

  const [previewLoading, setPreviewLoading] = useState(false);

  const updatePageFiles = useCallback(async () => {
    if (!projectId || !pageId) {
      return;
    }

    const fileTree = await trpcClient.webContainer.getFileTreeForPage.query({
      projectId,
      pageId,
    });
    await mountAdditionalFiles?.(fileTree);
  }, [projectId, pageId, mountAdditionalFiles]);

  const {
    mutateAsync: revertPageToVersion,
    isPending: isRevertingPageToVersion,
  } = trpc.nextJSProject.revertPageToVersion.useMutation({
    onSuccess: () => {
      void updatePageFiles();
    },
  });

  const { mutate: addMessage } = trpc.chat.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  const handleRevertPageToVersion = useCallback(async () => {
    if (projectId && fileVersionId && user?.id && chatId) {
      const result = await revertPageToVersion({
        projectId,
        fileVersionId,
        userId: user?.id,
      });

      addMessage({
        projectId,
        chatId,
        content: {
          type: "array",
          content: [
            {
              type: "text",
              contentType: "revert" as const,
              text: result.summary,
            },
          ],
        },
        role: "user",
      });
    }
  }, [
    projectId,
    fileVersionId,
    user?.id,
    revertPageToVersion,
    chatId,
    addMessage,
  ]);

  const handlePreviewVersion = useCallback(async () => {
    if (!projectId || !pageId || !fileVersionId) {
      return;
    }

    setPreviewLoading(true);

    const fileTree = await trpcClient.webContainer.getFileTreeForPage.query({
      projectId,
      pageId,
      fileVersionId,
    });
    await mountAdditionalFiles?.(fileTree);
    setViewingVersionId(fileVersionId);
    setPreviewLoading(false);
  }, [
    projectId,
    pageId,
    fileVersionId,
    setViewingVersionId,
    mountAdditionalFiles,
  ]);

  return (
    <Card className="bg-sheet w-64">
      <CardContent className="flex flex-row items-center gap-2">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted">
            <Loader className="size-5" />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {hasError ? (
              <XIcon className="size-5 text-danger" />
            ) : (
              <CheckIcon className="size-5 text-success" />
            )}
          </div>
        )}
        <span className="text-foreground font-medium text-sm grow">
          {!hasError ? message : "Error applying changes"}
        </span>
        {isDebug && appliedDiffs && appliedDiffs.length > 0 && (
          <Popover
            triggerAsChild
            shouldPreventDefaultOnInteractOutside={false}
            side="bottom"
            align="start"
            sideOffset={8}
            className="w-[500px]"
            title="Manage Changes"
            content={
              <div className="max-h-96 overflow-auto">
                <div className="text-sm">File version: {fileVersionId}</div>
                <div className="text-sm">
                  File version summary: {fileVersionSummary}
                </div>
                <Button
                  layoutClassName="my-5"
                  variant="secondary"
                  size="sm"
                  isLoading={isRevertingPageToVersion}
                  onClick={() => {
                    void handleRevertPageToVersion();
                  }}
                >
                  Revert to this version
                </Button>
                <Button
                  layoutClassName="my-5"
                  variant="secondary"
                  size="sm"
                  disabled={viewingVersionId === fileVersionId}
                  isLoading={previewLoading}
                  onClick={() => {
                    void handlePreviewVersion();
                  }}
                >
                  {viewingVersionId === fileVersionId
                    ? "Viewing Preview"
                    : "Preview this version"}
                </Button>
                {appliedDiffs.map((diff, index) => (
                  <pre
                    key={index}
                    className="text-xs bg-neutral-soft p-2 rounded border mb-2 whitespace-pre-wrap"
                  >
                    {diff}
                  </pre>
                ))}
              </div>
            }
          >
            <IconButton
              variant="secondary"
              icon={<MoreHorizontal className="size-4" />}
              tooltipText="View debug diffs"
              size="sm"
            />
          </Popover>
        )}
      </CardContent>
    </Card>
  );
};

export default ChangeBlock;
